/**
 * Canonical KPI navigation for /accounting/income.
 *
 * Single source of truth importable by both frontend (client/) and backend (server/).
 *
 * Exports:
 *  - IncomeKpiCategory — union type for all KPI category keys
 *  - KPI_CATEGORY_FILTER_MAP — category → {statuses, overdueFilter}
 *  - buildKpiUrl — canonical ?kpi= URL builder for dashboard cards and action paths
 *  - buildClearFiltersUrl — URL to clear all income filters
 *  - parseKpiFiltersFromUrl — URL-param parser with legacy ?overdue=yes compat
 *
 * No React, no DOM side-effects — safe to import from server services and tests.
 */

export const KPI_INCOME_BASE_PATH = "/accounting/income" as const;

export type IncomeKpiCategory =
  | "planned"
  | "pending_invoice_issue"
  | "invoice_registered_followup"
  | "collection_followup"
  | "overdue_uncollected"
  | "collected"
  | "cancelled"
  | "amount_unverifiable";

export type KpiFilterState = {
  statuses: string[];
  overdueFilter: "yes" | "no" | "all";
};

/**
 * Canonical mapping from KPI category to the query filter combination
 * that the accountingDashboard endpoint expects.
 *
 * Must stay in sync with classifyFinanceMilestone in financeKpiContract.ts.
 *
 * overdue_uncollected: invoiced OR pending_collection + overdue=yes
 * invoice_registered_followup: invoiced + overdue=no
 * collection_followup: pending_collection + overdue=no
 * amount_unverifiable: backend does not support direct filter; scaffold as no-op.
 */
export const KPI_CATEGORY_FILTER_MAP: Record<IncomeKpiCategory, KpiFilterState> = {
  planned: { statuses: ["pending"], overdueFilter: "no" },
  pending_invoice_issue: { statuses: ["ready_to_invoice", "accounting_notified"], overdueFilter: "no" },
  invoice_registered_followup: { statuses: ["invoiced", "sent_to_client"], overdueFilter: "no" },
  collection_followup: { statuses: ["pending_collection"], overdueFilter: "no" },
  overdue_uncollected: { statuses: ["invoiced", "sent_to_client", "pending_collection"], overdueFilter: "yes" },
  collected: { statuses: ["collected"], overdueFilter: "all" },
  cancelled: { statuses: ["cancelled"], overdueFilter: "all" },
  amount_unverifiable: { statuses: [], overdueFilter: "all" },
} as const;

const VALID_KPI_CATEGORIES = new Set<string>(Object.keys(KPI_CATEGORY_FILTER_MAP));

export function isValidKpiCategory(value: string): value is IncomeKpiCategory {
  return VALID_KPI_CATEGORIES.has(value);
}

/**
 * Parse KPI filters from a URL search string.
 *
 * Priority:
 * 1. ?kpi=<category> — canonical; translates to statuses + overdueFilter
 * 2. Legacy ?overdue=yes — translates to overdue_uncollected filter combo
 * 3. Legacy ?status=s1,s2 + ?overdue=yes|no — used as-is
 * 4. Prop defaults
 */
export function parseKpiFiltersFromUrl(
  search: string,
  defaults: { statuses: string[]; overdueFilter: "yes" | "no" | "all" },
): KpiFilterState {
  const params = new URLSearchParams(search);

  const kpiParam = params.get("kpi");
  if (kpiParam && isValidKpiCategory(kpiParam)) {
    return KPI_CATEGORY_FILTER_MAP[kpiParam];
  }

  const overdueParam = params.get("overdue");
  if (overdueParam === "yes") {
    return KPI_CATEGORY_FILTER_MAP.overdue_uncollected;
  }

  const statusParam = params.get("status");
  const statuses = statusParam
    ? statusParam
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : defaults.statuses;

  const overdueFilter: "yes" | "no" | "all" =
    overdueParam === "no" ? "no" : overdueParam === "all" ? "all" : defaults.overdueFilter;

  return { statuses, overdueFilter };
}

/** Build a canonical ?kpi= URL for a given KPI category. */
export function buildKpiUrl(category: IncomeKpiCategory): string {
  return `${KPI_INCOME_BASE_PATH}?kpi=${category}`;
}

/** URL to use for "Limpiar filtros" — removes all params. */
export function buildClearFiltersUrl(): string {
  return KPI_INCOME_BASE_PATH;
}
